import { z } from 'zod';

/**
 * Operator action audit (IMS Doctrine Integration). The doctrinal "scribe" trail
 * — every operator interaction logged with the COP context it happened in, so a
 * legal-discovery query can reconstruct what the operator saw. Posted
 * fire-and-forget to POST /api/operator-actions; the server adds operator_id,
 * timestamp, ip_address, and user_agent.
 *
 * action_type is a controlled vocabulary. Server-side it is enforced by a FK
 * from operator_actions.action_type into the valid_operator_action_types lookup
 * table (migration 0045 — replaced the old CHECK enum that parallel PRs kept
 * clobbering; Task #90). When a new interaction ships, extend BOTH this enum AND
 * the lookup table (one INSERT row, no CHECK rewrite). See
 * docs/migration-protocol.md.
 */
export const OPERATOR_ACTION_TYPES = [
  'toggle_layer',
  'click_fire',
  'view_fire_detail',
  'click_outage',
  'click_aircraft',
  'click_camera',
  'click_asset',
  'click_sensor',
  // --- High-risk hotspot render (E.2.3) ---
  'click_high_risk_hotspot',
  'view_high_risk_hotspot_detail',
  // --- Extreme Weather Warnings (EWW / L.0 Layer #2 blending) ---
  // Operator clicked a CAP polygon on the blended Urgent Anomalies surface.
  'click_extreme_weather',
  // A CAP alert detail panel opened.
  'view_extreme_weather_detail',
  // --- R.3 click-fire UX (detect → confirm → dispatch) ---
  // Operator clicks an incident → camera flies to a ~20 km incident viewport.
  'zoom_to_incident',
  // The Nearby Response Assets section rendered for an incident.
  'view_nearby_assets',
  // Confirm Fire / Recommend Dispatch buttons (now wired to POST a dispatch rec).
  'confirm_fire_clicked',
  'recommend_dispatch_clicked',
  // "Forward to Agency" on a dispatch recommendation — REAL delivery via the
  // §4.21 channel (migration 0047). Recommend → forward stays two distinct
  // operator actions; the system never auto-sends.
  'forward_dispatch_recommendation',
  // --- SITREP engine (SITREP.1 — Ontario IMS §2.2 "Situation Report") ---
  // Operator generated a SITREP (region/incident scope).
  'generate_sitrep',
  // A persisted SITREP detail view opened.
  'view_sitrep',
  // Copy-as-markdown / download of a SITREP.
  'export_sitrep_markdown',
  // "Forward to PEOC" clicked — STUBBED (nothing sent); audited regardless.
  'forward_sitrep_clicked',
  // SITREP v3.2 — a SITREP auto-issued by tsr-sitrep-scheduler-worker at an
  // operational-period boundary (midpoint progress report / final at close).
  // Distinct from operator-driven 'generate_sitrep' so the machine-issued trail
  // is separable in audit queries.
  'auto_generate_sitrep',
  // #300 (news-LLM council Concept D) — the operator asked the on-demand seam to
  // DRAFT the §4.19 Public-Information section from the wire for a tracked
  // incident. The audited act is the DRAFT, not a SITREP: it writes NO
  // sitrep_reports row — only an editable, cited candidate paragraph the operator
  // edits, owns, and folds in (or discards). Never auto-inserted / -forwarded.
  // Cost-caged + per-incident-cooled via the ask_the_wire_queries ledger (0081).
  'draft_media_sitrep',
  // --- Assistance Request workflow (AR.1 — Task #62, PEOC routing) ---
  // Operator submitted an assistance request on a requestor's behalf.
  'submit_assistance_request',
  // An assistance request detail view opened.
  'view_assistance_request',
  // #419 closed-loop — PEOC stamps "received" on an inbound request (the requestor
  // receipt), ORTHOGONAL to the status machine (migration 0113). Auto-back-filled on
  // triage; the explicit verb fires only for the can't-triage-yet ack. Audited under
  // the ACKNOWLEDGING (PEOC) operator, never the submitter.
  'acknowledge_assistance_request',
  // A status transition (triaged/routed/assigned/in_progress/fulfilled/closed).
  'update_assistance_request_status',
  // "Forward to PEOC" on an assistance request — REAL delivery (Task #79).
  'forward_assistance_request',
  // Request cancelled.
  'cancel_assistance_request',
  // Assign BINDS a real resource (Ruling 3 / Phase 3b-1, migration 0100): advancing
  // an assistance request to `assigned` raises a linked resource_request (mirrors the
  // R.3 dispatch call site, threading assistance_request_id back). Distinct from
  // update_assistance_request_status so the scribe trail separates the status advance
  // from the resource raise — both fire on one assign.
  'assign_assistance_resource',
  // SITREP v3.1 — incident + operational-period lifecycle.
  // Promote a fire/hotspot/CAP alert (or declare) to a tracked incident.
  'promote_incident',
  // Close a tracked incident.
  'close_incident',
  // Declare a new operational period on an incident.
  'declare_operational_period',
  // Transition an operational period (planned→active→closed).
  'transition_operational_period',
  // SITREP v3.3 (migration 0054) — operator set/changed a period's SITREP
  // auto-issuance cadence (sitrep_cadence_minutes) via PATCH. Distinct from the
  // declare/transition verbs so the cadence-config trail is separable in audit.
  'set_operational_period_cadence',
  // --- W.1 Wellness & Headcount keystone object (§8.3) ---
  // Declare a new community.
  'declare_community',
  // Edit a community's attributes.
  'edit_community',
  // Relocate a community's centroid on the map (geography-only PATCH; W.2 editor).
  'move_community',
  // Soft-retire a community (sets retired_at; no hard delete — W.2 editor).
  'retire_community',
  // Un-retire a community (clears retired_at; W.2 editor).
  'restore_community',
  // A community's StatCan CIMD 2021 quintiles were (re)resolved from the
  // official GOC service (on-declare, on-move, or refresh; migration 0068).
  'enrich_community_cimd',
  // Declare a point-in-time headcount snapshot for a community.
  'declare_headcount',
  // Create a wellness check (planned).
  'start_wellness_check',
  // Update a wellness check's status/metrics (→ in_progress / gap_identified).
  'update_wellness_check',
  // Move a wellness check to complete.
  'complete_wellness_check',
  'open_panel',
  'close_panel',
  'pan_map',
  'zoom_map',
  // --- L.2.2 Anomaly Queue sidebar (operator-cop-doctrine §3B.1, §6.1, §9.4) ---
  // ✓ Ack — row demotes to the collapsed Acknowledged section; marker stays.
  'acknowledge_anomaly',
  // Row micro-status advance (new → ack → working → resolving), payload {status}.
  'set_anomaly_status',
  // Owner / position assignment on a queue row (migration 0058, #140 — unified
  // anomaly_workstate; replaces the window.prompt owner path with a persisted chip).
  'set_anomaly_owner',
  // --- L.2.2 smoke_detected anomaly (§6.2) ---
  'click_smoke_detection_anomaly',
  'view_smoke_detection_anomaly_detail',
  // Confirm Smoke → promotes to a high_risk_hotspot.
  'confirm_smoke_detection',
  // Dismiss Smoke → false positive, auto-leaves the queue.
  'dismiss_smoke_detection',
  // --- L.2.2 Aircraft & Marine render (§3B.8 ambient register) ---
  'click_marine_vessel',
  'view_marine_vessel_detail',
  // --- L.2.2 Drone Operator Coverage (§3B.6) ---
  'click_drone_coverage',
  'view_drone_coverage_detail',
  // "Recommend Drone Inspection" — recommend→forward, never a direct tasking write.
  'recommend_drone_inspection',
  // --- R.4.1 Resource Status Lifecycle (operator-cop-doctrine §2.5/§6.4/§9.3) ---
  // Operator raised a resource request (also fired by R.3 "Recommend Dispatch").
  'request_resource',
  // pending → assigned (a hull is bound to the request).
  'assign_resource',
  // assigned → committed (the resource is committed to the incident).
  'commit_resource',
  // Cancel an open resource request.
  'cancel_resource_request',
  // Operator directly moved an asset between lifecycle states (no request flow).
  'update_resource_lifecycle_state',
  // Operator set an asset's readiness (ready/partial/unavailable/unknown).
  'update_resource_readiness',
  // Operator set an asset's time-to-deploy band (24–72h surge model).
  'update_resource_time_to_deploy',
  // Operator edited an asset's multi-valued capability tags.
  'update_resource_capability_tags',
  // --- E.W.0.5 Raw Hot Spots (pre-fusion satellite thermal, §3B.8 ambient;
  //     parallel PR #58 — unioned here so neither PR's operator_actions CHECK
  //     clobbers the other on merge; Task #90 protocol) ---
  // Operator clicked a raw (pre-fusion) thermal dot.
  'click_raw_hotspot',
  // The RawHotspotBody side-panel detail opened for one.
  'view_raw_hotspot_detail',
  // --- E.W.5 Camera Smoke Detections (pre-fusion camera-CV, §3B.8 ambient;
  //     migration 0057 — the raw confirm-stage camera_smoke_detections render,
  //     the camera-CV sibling of Raw Hot Spots; NOT the smoke_detected anomaly) ---
  // Operator clicked a camera-smoke detection dot.
  'click_camera_smoke',
  // The CameraSmokeBody side-panel detail opened for one.
  'view_camera_smoke_detail',
  // --- R.4.2 full 7-state lifecycle (check-in / staging / demob; §6.4 dec. a) ---
  // assigned → checked_in (resource reported in at the incident).
  'check_in_resource',
  // checked_in → staging (resource moved into a staging area).
  'transition_to_staging',
  // assigned|staging → committed (resource committed to the incident).
  'transition_to_committed',
  // committed → demobilized (resource released; terminal lifecycle state, §8).
  'demobilize_resource',
  // Operator declared a new staging area on an incident.
  'declare_staging_area',
  // Operator closed (soft-deleted) a staging area.
  'close_staging_area',
  // --- B.1 Briefing-Up view + delivery (operator-cop-doctrine §2.6/§3B.7/§6.3) ---
  // Operator exported a SITREP as a PDF artifact (server-side pdfkit render).
  'export_sitrep_pdf',
  // Operator minted a tokenized read-only Briefing-Up URL for an elected official.
  'create_briefing_token',
  // Operator revoked a briefing token before its TTL.
  'revoke_briefing_token',
  // tsr-sitrep-scheduler-worker auto-emailed an operational-period briefing
  // (SITREP narrative + PDF) to an incident's registered subscribers at a
  // period boundary. Machine-issued, distinct from any operator-driven forward.
  'auto_email_briefing',
  // --- #328 Phase 1: out-of-app official briefing (Ontario IMS §7.4 / App A #19
  // "briefing up" to senior and elected officials; App A #21 the ECG "may not be directly involved
  // in an EOC"). OPERATOR-triggered (distinct from the machine-issued
  // auto_email_briefing above) — a decision-maker who sits OUTSIDE the ops centre
  // is briefed by emailing them the SITREP OR by recording that they were reached
  // by phone. The channel ('email' | 'phone') rides on the payload; both persist an
  // in-COP record (App A #72 record-keeping duty). Register is INFORM/BRIEF, never a
  // command of the recipient (§2.2). ---
  'brief_official',
  // --- Task #92 (unioned from R.4.x — keep the vocabulary in lockstep so neither
  // PR's operator_actions CHECK clobbers the other) — active_fire Recommend
  // Dispatch → incident linkage. R.3 "Recommend Dispatch" on an active_fire
  // auto-promoted the fire to a new tracked incident (so the raised
  // resource_request has an incident FK). Only fired when a NEW incident is
  // created; reusing an existing one is silent.
  'auto_promote_fire_to_incident',
  // --- Task #64 sectoring overlay (IMS §5.3 divisions/sectors; operator-cop-
  //     doctrine §2.1/§2.3/§4.3, §6.6 free-text lead-label decision) ---
  // Operator drew a new sector polygon on an incident.
  'draw_sector',
  // Operator redrew an existing sector's polygon (geometry edit).
  'edit_sector_geometry',
  // Operator edited a sector's name / lead_label / notes.
  'edit_sector_metadata',
  // Operator set/changed a sector's free-text lead label (also fired by the
  // metadata edit when lead_label specifically changes — §6.6).
  'assign_sector_lead',
  // Operator soft-closed a sector (terminal; closed_at set).
  'close_sector',
  // --- Power Outages render (Ice Storm Risk → "Power Outages"; migration 0048,
  //     operator-cop-doctrine §3B.8/§3A.7/§4.5). The ambient utility_outages
  //     layer's two interactions. (Distinct from the early `click_outage` stub.) ---
  // Operator clicked an outage marker / service-area polygon.
  'click_utility_outage',
  // The outage detail panel opened (GET /api/utility-outages/:id).
  'view_utility_outage_detail',
  // --- Road Closures render (Ice Storm Risk → "Road Closures"; migration 0055,
  //     operator-cop-doctrine §3B.8/§3A.7/§4.5). The ambient road_closures layer's
  //     two interactions (511 ON/AB + DriveBC BC). ---
  // Operator clicked a road-closure marker / affected segment.
  'click_road_closure',
  // The road-closure detail panel opened (GET /api/road-closures/:id).
  'view_road_closure_detail',
  // --- Estimated Comms Degradation render (Ice Storm Risk → "Estimated Comms
  //     Degradation"; #109, migration 0062). The ambient comms_degradation_zones
  //     estimate-zone layer's map interaction. ---
  // Operator clicked an estimated comms-loss zone polygon / centroid label.
  'click_comms_degradation_zone',
  // --- Ice Storm Fusion Anomaly (migration 0049, operator-cop-doctrine §4.5/§3B.8,
  //     IMS §7.2 anticipatory activation). The act-register anomaly on top of the
  //     ambient Power Outages layer: a clustered-outage + freezing-rain/ice-storm
  //     CAP signal the operator acknowledges or dismisses. ---
  // Operator clicked an ice-storm anomaly marker / queue row.
  'click_ice_storm_anomaly',
  // The ice-storm anomaly detail panel opened (GET /api/ice-storm-anomalies/:id).
  'view_ice_storm_anomaly_detail',
  // Operator acknowledged the anomaly (queue demote; marker stays).
  'acknowledge_ice_storm_anomaly',
  // Operator dismissed the anomaly as a false positive (auto-leaves the queue).
  'dismiss_ice_storm_anomaly',
  // --- Flood Fusion Anomaly (migration 0053, operator-cop-doctrine §2.7/§4.4/§3B.8,
  //     IMS §7.2). The act-register anomaly on top of the ambient Hydrometric Gauges
  //     layer: a gauge sustained rate-of-rise + community-in-reach signal the operator
  //     acknowledges or dismisses. ---
  // Operator clicked a flood anomaly marker / queue row.
  'click_flood_anomaly',
  // The flood anomaly detail panel opened (GET /api/flood-anomalies/:id).
  'view_flood_anomaly_detail',
  // Operator acknowledged the anomaly (queue demote; marker stays).
  'acknowledge_flood_anomaly',
  // Operator dismissed the anomaly as a false positive (auto-leaves the queue).
  'dismiss_flood_anomaly',
  // --- Evacuation Logistics Surface (migration 0051, operator-cop-doctrine
  //     §2.5 Logistics / §4.4 flood air-evac). The incident-scoped panel that
  //     assembles linked communities + headcount, staging areas, aviation
  //     assets + airlift aircraft, and overlapping CAP alerts. ---
  // Operator opened the incident-scoped Evacuation Logistics panel.
  'view_evacuation_logistics',
  // Operator flew the camera to a linked community from the evacuation panel.
  'fly_to_community',
  // --- Unified search surface (#139 / #141, migration 0059). The search box /
  //     Cmd-K palette: an operator typed a query and dispatched a result —
  //     either a geocoded place (fly to its bbox) or an operator-domain entity
  //     (camera / asset / fire / community / incident) routed through the
  //     existing deep-link handlers. ---
  'search_and_fly',
  // --- Re-alarm on escalation (#129, migration 0060, design-council Part II
  //     "Alarm Lifecycle Law" rule 3). The SYSTEM (fusion worker) clears an
  //     operator's acknowledgement when an acked anomaly's condition WORSENS
  //     (confidence or Response Level rises, or a CAP severity upgrade) so the
  //     marker returns to URGENT-RED + pulse as a fresh alarm event. The one
  //     mandatory companion to the founder downgrade-recolour rule — an ack must
  //     never permanently silence an escalating hazard. operator_id =
  //     'fusion-worker'. ---
  'clear_ack_on_escalation',
  // --- Operator saved views (#154 named regions + #155 home-view server sync,
  //     migration 0061). The home view + named region bboxes the operator saves
  //     to the server so they follow them across devices (lifting the #149
  //     localStorage-only home view onto the operator_saved_views table). Writes
  //     audit client-side; the route is pure persistence. ---
  // Operator set / replaced their home view ("Set home").
  'save_home_view',
  // Operator saved a named region bbox ("NW Ontario", "GTA").
  'save_region_view',
  // Operator deleted a saved region (or cleared their home view).
  'delete_saved_view',
  // Operator flew the camera to a saved home / region from the VIEW group.
  'fly_to_saved_view',
  // --- Evacuation Orders & Alerts render (#204, Hazard group → "Evacuation Orders
  //     & Alerts"; migration 0064). The ambient arcgis_public_features evacuation
  //     layer (GeoBC / EMBC order & alert AREA polygons). Two interactions. ---
  // Operator clicked an evacuation order/alert area polygon.
  'click_evacuation_order',
  // The evacuation-order detail panel opened (GET /api/arcgis-features/:id).
  'view_evacuation_order_detail',
  // --- Generalized public-ArcGIS category render (#213, migration 0065). The
  //     config-driven arcgis renderer that surfaces the remaining ingested feeds
  //     (Alberta fire bans/restrictions, drought, boil-water advisories, BC State
  //     of Local Emergency, tropical cyclones) as their own COP layers. ONE pair of
  //     generic verbs for every non-evacuation category; the category travels in the
  //     audit payload (`category`), so a new category never needs a new verb. ---
  // Operator clicked an ambient arcgis_public_features area/point (any category).
  'click_arcgis_feature',
  // The generic arcgis-feature detail panel opened (GET /api/arcgis-features/:id).
  'view_arcgis_feature_detail',
  // --- Seat role-profiles (#187, migration 0066). Named bundles of layer-pref
  //     DELTAS + panel arrangement per IMS seat (Situation Unit / Logistics /
  //     Liaison, doctrine §2.4/§2.5). The 3 curated seeds are code constants;
  //     custom profiles persist as operator_saved_views kind='profile'. Writes
  //     audit client-side; the route is pure persistence. Per-operator pref
  //     only — NOT an auth model (RBAC out of scope). ---
  // Operator applied a seat profile (seed or custom) from the Settings overflow.
  'apply_seat_profile',
  // Operator saved the current workspace as a named profile.
  'save_seat_profile',
  // Operator deleted a custom saved profile.
  'delete_seat_profile',
  // --- 3D flythrough audit (#235, migration 0067 — the #179 follow-up). The
  //     operator-triggered descend-and-orbit camera tour over on-demand
  //     photoreal tiles now brackets into the scribe record (feeds the #180
  //     AAR replay). ---
  // Operator started a 3D flythrough from a detail panel (payload: lon/lat/label).
  'start_flythrough',
  // The flythrough ended (payload.reason: 'stopped' = operator clicked End ·
  // 'completed' = tour finished on its own · 'destroyed' = viewer unmounted).
  'stop_flythrough',
  // --- AAR replay audit (#240, migration 0070 — the #180 follow-up). The
  //     time-replay open/close now brackets into the scribe record, mirroring
  //     the flythrough pair. ---
  // Operator opened the AAR time replay on an incident (payload: incident_id /
  // incident_number — the incident_id key also scopes these rows back into
  // that incident's own future replays).
  'start_aar_replay',
  // The replay ended (payload.reason: 'stopped' = operator exited ·
  // 'destroyed' = viewer unmounted).
  'stop_aar_replay',
  // --- News & Social operator surface (#65/#183 wave 3b, migration 0071).
  //     The first PROMOTION semantics on the 0069 media_signals wire — both
  //     operator-gated by design (never auto-anything). The dismissed set
  //     itself persists in media_signal_dismissals (workflow state); these
  //     verbs are the audit record. ---
  // Operator pinned a wire signal to the media region / wall (kind:'news'
  // pin path; payload: signal_id / source / dedup_key).
  'pin_media_signal',
  // Operator hid a signal from the News & Social feed (payload: signal_id /
  // source).
  'dismiss_media_signal',
  // Operator un-dismissed a media signal via the transient "Dismissed — Undo"
  // beat (#251, migration 0075; payload: signal_id / source). Dismissal stays
  // GLOBAL across seats — per-seat scoping is deferred to RBAC (#190).
  'restore_media_signal',
  // --- Media-signal igniter (#184, migration 0072). A geo-clustered chatter
  //     spike (media_signals wire) raised a LOW-CONFIDENCE "needs assessment"
  //     media_signal_anomaly into the Urgent Alerts queue; the operator's two
  //     decisions. NEVER auto-scores a Response Level. ---
  // Operator assessed the chatter spike as real (payload: anomaly_id /
  // hazard_key / signal_count / source_count). The row stays in the queue
  // (the ice 'acknowledged' analogue) until the cluster dissipates.
  'confirm_media_signal_anomaly',
  // Operator judged the spike noise (payload: anomaly_id / reason).
  'dismiss_media_signal_anomaly',
  // --- Wall risk tour (#272, design-council PART XI Ruling 2.6, migration
  //     0076). The wall's hourly tour SITREP card surfaces NEW-since-cursor
  //     domain items; clearing happens at the DESK only (wall read-only law).
  //     Each stop's cleared-through cursor is DERIVED READ-TIME from the
  //     latest such row (the #119 SITL precedent — no new table): an
  //     auditable "the room reviewed fire risk at 14:45" record. ---
  // Operator cleared a wall tour stop's SITREP card from the desk COP
  // (payload: stop / cleared_through ISO timestamp).
  'clear_wall_tour_card',
  // --- Wall curation (#273, design-council PART XI Ruling 5.2, migration
  //     0077). The wall reel draws AMBIENT seats featured-first; the curated
  //     pool is a first-class `cameras.wall_featured` column, set/unset from
  //     the camera panel at a DESK (the wall read-only law) via this audited
  //     verb pair (payload: camera_id). Ingest NEVER writes wall_featured. ---
  'feature_camera_on_wall',
  'unfeature_camera_on_wall',
  // --- Drone D1 #319 mission lifecycle (migration 0091) + #321 dispatch (migration 0092).
  //     The scribe-stamped state machine on the #317 drone_missions record:
  //     requested → accepted → confirmed → enroute → flown → billed → paid (declined /
  //     cancelled terminal). Each verb is one transition; the actor guard (requestor vs
  //     operator) + valid edges live in the pure drone-mission.ts state machine (typed
  //     `verb: OperatorActionType`, so a missing verb fails the build). payload carries
  //     mission_id + from/to status. ---
  // Requestor (gov/EOC) raised a mission from a #320 fit-ranked pick.
  'drone_mission_requested',
  // Operator accepted a mission assigned to them WITH TERMS (requested → accepted).
  'drone_mission_accepted',
  // Requestor confirmed the operator's accept-terms (accepted → confirmed). (migration 0092)
  'drone_mission_confirmed',
  // Operator dispatched the asset / marked it enroute (confirmed → enroute). (migration 0092)
  'drone_mission_enroute',
  // Operator declined a mission (requested → declined; payload.reason optional).
  'drone_mission_declined',
  // Operator marked a mission flown (enroute → flown); writes the usage-ledger row.
  'drone_mission_flown',
  // Requestor cancelled a mission before it flew (requested|accepted|confirmed|enroute → cancelled).
  'drone_mission_cancelled',
  // Requestor recorded a flown mission as billed (flown → billed; ledger status sync).
  'drone_mission_billed',
  // Requestor recorded a billed mission as paid (billed → paid; ledger status sync).
  'drone_mission_paid',
  // --- OUTBOUND agency_tasks (#379, Coordination Re-Arch Phase 3b-2a, migration 0101).
  //     The PEOC tasks a NAMED, NON-SELF agency DOWN (Ruling 2). The full lifecycle verb
  //     set is minted here so the pure agency-task.ts state machine (typed
  //     `verb: OperatorActionType`) compiles; 3b-2a wires only the requester-side verbs
  //     (issue / cancel), 3b-2b wires the target-side response handlers (acknowledge /
  //     accept / decline / start / complete) — no new verb migration there. payload
  //     carries agency_task_id + target_agency_id + from/to status. ---
  // PEOC issued a down-task to a non-self agency (→ issued).
  'issue_agency_task',
  // Target agency acknowledged receipt (issued → acknowledged).
  'acknowledge_agency_task',
  // Target agency accepted the task (issued|acknowledged → accepted).
  'accept_agency_task',
  // Target agency declined the task (issued|acknowledged → declined; payload.reason optional).
  'decline_agency_task',
  // Target agency started work (accepted → in_progress).
  'start_agency_task',
  // Target agency completed the task (in_progress → completed).
  'complete_agency_task',
  // Requesting PEOC cancelled the task before completion (issued|acknowledged|accepted|in_progress → cancelled).
  'cancel_agency_task',
  // #425 — an incident promoted from a non-fire anomaly/alert (flood/ice/CAP) via the
  // un-gated Declare affordance. Fire/hotspot promotions keep emitting 'promote_incident'
  // (byte-stable) — this verb makes the new declare-from-any-hazard path separately auditable.
  'promote_incident_from_anomaly',
  // --- Operator-declared evacuation-order lifecycle (#445-(3), migration 0115) ---
  // A first-class operator evac-order the COP RECORDS on behalf of a named issuing
  // authority (municipal / First Nation Chief & Council / province). Distinct from the
  // render verbs click_evacuation_order / view_evacuation_order_detail (the EXTERNAL
  // GeoBC layer, migration 0064) — these three are the issue/escalate/lift lifecycle.
  'declare_evacuation_order',
  'escalate_evacuation_order',
  'rescind_evacuation_order',
  // #459 — the CORRECT paths: an operator-declared evac order is a living record. A
  // mis-drawn area redraws (edit_evacuation_order_geometry); a fat-fingered authority /
  // name / population / coordinating attribution edits (edit_evacuation_order_metadata).
  // Mirror the sectors edit verbs; NOT a rescind — the escalate/rescind lifecycle is intact.
  'edit_evacuation_order_geometry',
  'edit_evacuation_order_metadata',
  // --- Municipal out-of-app notifications (#6a, migration 0116) ---
  // The audited CONSENT trail for jurisdiction-scoped SMS. ONE verb covers the
  // whole consent lifecycle — opt-in requested, PIN-confirmed, per-trigger edit,
  // operator opt-out, and the inbound STOP/ARRET (actor 'sms-status'). The
  // payload carries { trigger, from, to, basis } so revocation is AAR-provable
  // (the council §6 awareness gap; co-equal with #419).
  'update_notification_subscription',
  // --- Incident lifecycle (R1/#477, migration 0117; values renamed by #486/0118) ---
  // BIDIRECTIONAL phase transition on incidents.lifecycle_state
  // (detected→confirmed→owned→response→deactivated + one-step de-escalation /
  // reignition). Payload carries { from, to } so the AAR can replay the arc.
  'advance_incident_lifecycle',
  // --- Evacuation = incident ATTRIBUTE (R2/#478, migration 0119) ---
  // Operator manually LINKED an evacuation order / life-safety CAP to an incident via
  // the "Link to incident" dropdown — it collapses into the incident as a protective-
  // action overlay (NOT a competing queue row). Linking to a recently-deactivated
  // incident REIGNITES it (advance_incident_lifecycle deactivated→confirmed). Distinct
  // from AUTO-link, which is a pure read-time projection and is never audited (nothing
  // persists, no operator acted). Payload: { kind, alert_id, incident_id }.
  'link_alert_to_incident',
  // Operator UNLINKED an evac/CAP from its incident (always-reversible mandatory
  // unlink; sets incident_link_mode='detached' to suppress re-auto-link — the Elliot
  // Lake 0-distance mis-bind). Payload: { kind, alert_id, was_incident_id }.
  'unlink_alert_from_incident',
  // --- W3 OWNERSHIP SPINE: structured Incident Command (single command, migration 0120) ---
  // ASSUME command (Ontario IMS §4.5 single command / §4 "the first arriving responder
  // immediately assumes the role of incident commander"): creates the LEAD
  // incident_command row (is_lead=true) AND advances the incident confirmed→owned in
  // one act — so R1 'owned' ⟺ ≥1 live command row. Payload carries the lead organization
  // + officer: { incident_id, agency_id, officer_name, from:'confirmed', to:'owned' }.
  'assume_incident_command',
  // RELEASE command: releases a live participation. For the sole lead this reverts the
  // incident owned→confirmed (the symmetric down-act); for a unified co-lead it returns the
  // command toward single command (unified→false at N=1) while the incident stays owned.
  // Payload: { incident_id, agency_id, from, to } | { incident_id, agency_id, participant_release:true }.
  'release_incident_command',
  // --- W3 UNIFIED COMMAND (§4.7) + TRANSFER OF COMMAND (§4.8), migration 0122; two-sided 0147 ---
  // INVITE TO UNIFIED COMMAND (§4.7, migration 0147 — the #481 MUST-2 two-sided accept): the
  // current lead ISSUES a unified-command invite (an incident_command_transfers row,
  // kind='unified_invite') to a 2nd commanding lead organization. NO co-lead is created yet —
  // §4.7 requires MUTUAL agreement, so co-command establishes only when the invitee accepts.
  // Payload: { incident_id, invite_id, from_agency_id, to_agency_id, functional_role }.
  'invite_to_unified_command',
  // ENTER UNIFIED COMMAND (§4.7): the INVITEE ACCEPTS a pending unified invite (on the shared
  // acknowledge-transfer route) — the co-lead participation is INSERTed (is_lead=false, unified=
  // true) with its Operations branch + the signed agreement; the whole command becomes unified.
  // One shared command, not N owners (§2.10). As of 0147 this fires on ACCEPT, not on invite.
  // Payload: { incident_id, agency_id, functional_role, unified:true }.
  'join_unified_command',
  // TRANSFER OF COMMAND (§4.8): the outgoing lead issues a hand-over of the single lead to
  // another commanding organization with a REQUIRED detailed hand-over briefing. Command does
  // NOT move until acknowledged. Payload: { incident_id, transfer_id, from_agency_id, to_agency_id }.
  'transfer_incident_command',
  // ACKNOWLEDGE TRANSFER (§4.8): the receiving IC accepts the pending hand-over; in one act the
  // transfer is stamped acknowledged AND the lead moves A → B (one owner before, one after).
  // Payload: { incident_id, transfer_id, from_agency_id, to_agency_id }.
  'acknowledge_incident_transfer',
  // --- R5 AREA COMMAND SPINE (Ontario IMS §4.6 — oversight across MULTIPLE sites, migration 0123) ---
  // ESTABLISH AREA COMMAND (§4.6): create the parent oversight entity (is_area_command=true,
  // hazard-agnostic) — a named Area Command that aggregates N child incidents; each child keeps
  // its OWN command/lifecycle/SITREP. A first-class entity that may exist with zero children (NOT
  // folded into first placement). "Complex" is backend-only; the UI reads "Area Command" (§4.6).
  // Payload: { incident_id, incident_number, name }.
  'establish_area_command',
  // PLACE UNDER AREA COMMAND (§4.6): set a child incident's parent_incident_id to the Area Command
  // (idempotent — re-placing under the same parent is a no-op; a child under a DIFFERENT parent is
  // a 409). Payload: { parent_incident_id, child_incident_id }.
  'place_under_area_command',
  // REMOVE FROM AREA COMMAND (§4.6): null a child's parent_incident_id (always-reversible detach;
  // the child survives, un-grouped). Payload: { child_incident_id, was_parent_incident_id }.
  'remove_from_area_command',
  // --- DECLARATION & NOTIFICATION Slice 1 (§2.1 statutory declaration, migration 0124) ---
  // DECLARE EMERGENCY: capture a declaration of emergency ON BEHALF OF the declaring authority
  // (records-not-confers). Slice 1 = the municipal path (declarer = the Head of Council); the
  // first_nation branch (BCR) is Declaration Slice 3. A PARALLEL SIBLING to an evac order + a
  // public alert — never a precondition of either. Payload: { declaration_id, declaration_number,
  // authority_kind, declaring_authority_name, incident_id }.
  'declare_emergency',
  // TERMINATE DECLARATION OF EMERGENCY: flip status declared → terminated.
  // Payload: { declaration_id, reason }.
  'terminate_emergency_declaration',
  // NOTIFY THE PEOC (the #490 pre-province bridge): the explicit, delegable notify act. Routes
  // through delivery.ts to the default PEOC destination (console_log/download/email + a
  // delivery_log row). ONE shared verb, disambiguated by the row's authority_kind (NOT split
  // municipal/FN). Payload: { declaration_id, delivery_log_id, call_confirmed, call_notes }.
  'notify_peoc_declaration',
  // --- DECLARATION & NOTIFICATION Slice 2 (public-alert REQUEST to the PEOC, migration 0125) ---
  // REQUEST PUBLIC ALERT: capture + route a public-alert REQUEST to the PEOC ON BEHALF OF the
  // requesting authority (records-not-confers — the PEOC is the only issuer, PERP §6). BOTH the
  // municipal and first_nation paths are wired (each is a request TO the PEOC — never a direct
  // NAAD/NPAS issuer, which is #461). The act routes through delivery.ts (the #490 bridge).
  // Payload: { request_id, request_number, authority_kind, requesting_authority_name, incident_id,
  // delivery_log_id, delivery_channel, call_confirmed, call_notes }.
  'request_public_alert',
  // RESCIND PUBLIC ALERT REQUEST: flip status active → rescinded (reverses the request) and route
  // a withdrawal notice to the PEOC. Payload: { request_id, request_number, reason, delivery_log_id }.
  'rescind_public_alert_request',
  // --- DECLARATION Slice 4 (Bill-25 s.4(10) post-termination report, migration 0127) ---
  // RECORD POST-TERMINATION REPORT: the COP RECORDS (on behalf of the head of council — does NOT
  // submit) the re-enacted EMCPA s.4(10) written report to the Minister after a MUNICIPAL
  // declaration is terminated. Stamps post_termination_report_submitted_at + author/recipient
  // columns. MUNICIPAL only (FN not subject to the EMCPA, PERP §1.3.2). The Slice-4 UI is gated on
  // BILL25_S4_IN_FORCE; this route is the live plumbing (inert while the flag is off). Payload:
  // { declaration_id, declaration_number, recipient, author_kind }.
  'record_post_termination_report',
  // --- W4b PEOC ARRIVALS (council ruling A.6, migration 0128) ---
  // ACKNOWLEDGE PEOC ARRIVAL: the PEOC RECEIPT act on a first-notice arrival (an incoming
  // emergency declaration, an FN PEOC/ISC dual-notify, or an Area-Command establishment).
  // Records receipt ONLY (App A #46 Liaison RECEIVES + coordinates support — it NEVER
  // approves/commands/adjudicates, PERP §6.8.1.2); NEVER an App A #9 check-in (no resource
  // status is touched). Idempotent (UPSERT on (source_type, source_id)); audited under the
  // ACKNOWLEDGING PEOC operator. Payload: { source_type, source_id }.
  'acknowledge_peoc_arrival',
  // --- R5 AREA COMMAND Slice 5 (§4.6 / App A #2, migration 0129) ---
  // SET AREA COMMAND OBJECTIVES: replace the Area Command PARENT's ordered list of broad
  // objectives (App A #2: "develop broad objectives for their assigned area"). On the
  // is_area_command parent ONLY — NEVER a child incident's IAP. Replace-whole-ordered-list
  // (DELETE + re-INSERT). Payload: { incident_id, count }.
  'set_area_command_objectives',
  // SET AREA COMMAND RESOURCE PRIORITIES: replace the Area Command PARENT's ordered list of
  // priorities for the sharing of critical resources across incident sites (App A #2). A
  // priority STATEMENT — never an allocation/tasking of a child (§4.6: the AC "works with"
  // the IMTs). Payload: { incident_id, count }.
  'set_area_command_resource_priorities',
  // --- W4c DUTY WATCH (coordination council A.3 / #526, migration 0130) ---
  // SET DESK WATCH: a desk marks (or clears) "eyes on this file" on an incident /
  // assistance-request / agency-task surface. ONE verb — covers watch-on AND stop-watch via
  // the `active` payload. Watching is NOT command (App A #11), NOT ownership (the signed §4.3
  // handle), NOT the Situation Unit function (App A #76); NON-EXCLUSIVE + additive (a watch
  // never evicts another desk, never a claim). Payload: { source_type, source_id, active }.
  'set_desk_watch',
  // --- #450 RESIDENT PUBLIC-INFORMATION channel (§7.10 · App A #62; EIO §2.6 / App A #23,
  //     migration 0133) ---
  // PUBLISH PUBLIC INFORMATION: a municipal Emergency Information Officer (EIO) publishes a
  // public-information RELEASE to the municipality's OWN opted-in resident list over its OWN
  // channel (§7.10 / App A #62 "shares messages with the public directly"). The municipality
  // ISSUES its own release; it is NOT Alert Ready (province-only, REQUESTED via #497's
  // request_public_alert → PEOC). Payload: { release_id, community_id, incident_id,
  // recipient_count, capped_count }.
  'publish_public_information',
  // --- #328 PHASE 2 TWO-WAY INTER-OPS THREAD (PERP §5.4 / §6.7; council A.5–A.8,
  //     migration 0135) ---
  // SEND INTER-OPS MESSAGE: a seat sends a context-attached (incident | assistance_request |
  // standalone) message, FANNED OUT to N recipients — in-app COP agencies (municipal EOC,
  // First Nation, the PEOC) and/or out-of-app registry destinations (ISC, Public Safety
  // Canada, ministries). kind 'information' | 'request_for_assistance' (PERP §6.7.1 — the
  // ministry/federal decides deployment). The FN one-act PEOC+ISC preset fans to both in one
  // send (PERP §6.7.1). Payload: { message_id, context_type, context_id, kind, recipient_count,
  // in_app_count, out_of_app_count }.
  'send_interops_message',
  // ACKNOWLEDGE INTER-OPS MESSAGE: the per-recipient RECEIPT act (records receipt ONLY —
  // council A.6; NEVER a command/approval, never mutates the message or any source row).
  // Idempotent (WHERE acknowledged_at IS NULL). Payload: { message_id, recipient_id }.
  'acknowledge_interops_message',
  // --- #547 RESIDENT SELF-OPT-IN (public /subscribe double-opt-in, migration 0136) ---
  // RESIDENT SUBSCRIBE: a resident requests a confirmation PIN on the public /subscribe page —
  // a PENDING self-subscribe to a community's emergency notifications (consent_source 'self').
  // Payload: { community_id, phone_masked, cooldown | capped }.
  'resident_subscribe',
  // RESIDENT CONFIRM: the texted PIN is entered on the page (pending → opted_in; consent_source
  // 'self', consented_at stamped). Payload: { community_id, phone_masked }.
  'resident_confirm',
  // RESIDENT UNSUBSCRIBE: a resident unsubscribes (→ opted_out; STOP parity). Payload:
  // { community_id | via_token, phone_masked }.
  'resident_unsubscribe',
  // --- #567 RECORD THE OUT-OF-APP RESPONSE (migration 0138) ---
  // RECORD INTER-OPS RESPONSE: the operator pastes back a reply received OUT OF APP (an RFA
  // email reply from Public Safety Canada / a ministry) on an out-of-app leg of an inter-ops
  // message — it joins the thread as a RECORDED DOCUMENT (kind 'recorded_response'), closing
  // the founder §1 workflow-completeness loop (the comms starts AND finishes in the COP). A
  // RECORD, never a send: no delivery fan-out, and the original leg's "Sent" state is UNCHANGED
  // (recording a reply never fabricates a read/ack on the outbound leg — #328p2 R3 / A.6). The
  // audited scribe act (App A #72). Payload: { message_id, reply_message_id, recipient_id,
  // destination_id }.
  'record_interops_response',
  // --- #453 PER-MESSAGE READ RECEIPT on the #420a community thread (migration 0139) ---
  // ACKNOWLEDGE COMMUNITY MESSAGE: the per-message RECEIPT act on the ambient PEOC↔community
  // coordination log — an EXPLICIT operator acknowledge (never auto-on-render). Records
  // receipt ONLY (council A.6 idiom; NEVER a command/approval, never mutates the message).
  // Idempotent (ON CONFLICT DO NOTHING on (message_id, reader_operator_id)); audited under the
  // acknowledging operator. A receipt exists only for a real in-app acknowledge. Payload:
  // { community_id, message_id }.
  'acknowledge_community_message',
  // --- #568 RECORD THE OFFICIAL'S RESPONSE (brief-official path, migration 0141) ---
  // RECORD OFFICIAL RESPONSE: the operator records what a briefed decision-maker outside the
  // ops centre (mayor / head of council / ECG — §7.4 / App A #19 "briefing up") said back after
  // a #549 brief-official send (email a SITREP / record a phone contact). It joins the briefing
  // history read from operator_actions, closing the founder §1 workflow-completeness loop (the
  // brief starts AND finishes in the COP). The audited scribe act (App A #72). A RECORD, never
  // proof of receipt — recording a response NEVER claims the official acknowledged / read /
  // approved the Situation Report (the #549 SENT-not-ACK honesty rule holds). The brief-official
  // family has its own data model (a scribe operator_actions row), distinct from #567's inter-ops
  // 'recorded_response' message kind. Payload: { sitrep_id, channel, official, received_at_local,
  // note }.
  'record_official_response',
  // --- #462 EVACUATION ACCOUNTING BRIDGE (order → incident → §4.30 accounting, migration 0143) ---
  // START EVACUATION ACCOUNTING originated a NEW incident FROM an operator evacuation order because
  // there was no proximate open hazard incident to attach the order to. The §4.30 accounting rollup
  // keys on an incident-tied community_headcount; this verb records the case where the order itself
  // became the incident's source record (source_record_type='operator_evac_order'), persisting the
  // order→incident link the accounting reads. The ATTACH path (a proximate open incident existed)
  // reuses 'link_alert_to_incident' — no incident is declared there, so it is NOT audited as this.
  // Payload: { order_id, incident_id, incident_number, hazard_type }.
  'declare_incident_from_evac_order',
  // --- #445-1 air-support (0145) ---
  // AIR-SUPPORT REQUEST to the MNRF/AFFES fire centre (records-not-confers). In Ontario a
  // waterbomber / air-support aircraft is an MNRF/AFFES asset TASKED only by the fire centre
  // (PERP §6.7.1); a municipal / Field-IC seat REQUESTS air support, routed TO the fire centre
  // — it NEVER tasks/dispatches aircraft. Clones the #497/#461 public-alert-request rails, NOT
  // the drone accept/enroute/flown machine. A SEPARATE act — never a side effect of
  // declare/own/promote or the escalation scorer. Payload: { request_id, request_number,
  // requesting_authority_kind, requesting_authority_name, requested_resource, incident_id,
  // delivery_log_id, delivery_channel, destination_name }.
  'request_air_support',
  // RESCIND AIR-SUPPORT REQUEST: flip status active → rescinded (reverses the request) and route
  // a withdrawal notice to the fire centre. Payload: { request_id, request_number, reason, delivery_log_id }.
  'rescind_air_support_request',
  // STAND DOWN AIR-SUPPORT REQUEST: flip status active → stood_down (the need has passed) and route
  // a stand-down notice to the fire centre. Payload: { request_id, request_number, reason, delivery_log_id }.
  'stand_down_air_support_request',
  // --- #331 reversible per-anomaly ASSESSMENT axis (migration 0146) ---
  // The operator's reversible triage disposition on an anomaly_workstate row —
  // unconfirmed ⇄ confirmed ⇄ false_positive — DISTINCT from acknowledge_anomaly (the
  // ✓ Ack triage-demote) and from confirm_fire_clicked (the R.3 dispatch promotion, which
  // raises a dispatch recommendation). These NEVER raise a dispatch recommendation. Each
  // transition emits ONE append-only ledger row server-side, atomic with the state write,
  // so history is never lost. On flood/ice a dismiss_anomaly mirrors the source status to
  // 'dismissed' so the #249 re-alarm override re-raises an escalating dismissed anomaly.
  // Payload: { source_type, source_id, from, to, reason? }.
  'confirm_anomaly',
  'reopen_anomaly',
  'dismiss_anomaly',
  // --- #445-ws2 operator-ASSESSED incident Response Level (migration 0148) ---
  // The Incident Commander's professional-judgment Response Level ASSESSMENT on an
  // incident (Ontario IMS §5 Table 1 / §6.1) — records-not-confers, SEPARATE from the
  // system-derived (which is never mutated). The operator-facing word (Set / Escalate /
  // De-escalate — NEVER "Declare") is derived from the transition direction; the backend
  // records one Set verb per direction + a Clear. Append-only {from,to} ledger, atomic
  // with the incidents cache write (mirrors #331). Gated to the owning command seat (the
  // IC); an EOC/PEOC coordinates, it does not counter-set. Payload: { incident_id,
  // incident_number, from, to, reason, assessed_by }.
  'set_incident_response_level',
  'clear_incident_response_level',
  // --- #645 (G): the HOST ARRIVAL ATTESTATION (migration 0156) ---
  // The host community's governing agency (or the PEOC) records an AGGREGATE, ATTRIBUTED,
  // NON-PII count of evacuees checked in at its reception ("N members attested at <host>,
  // <time>"). It NEVER writes the sending community's headcount — two registers, neither
  // overwrites the other; a disagreement renders UNRECONCILED. NO per-person/PII data.
  // Payload: { sending_community_id, host_community_id, persons, attested_at, incident_id? }.
  'attest_evacuee_arrival',
  // --- #612 After-Action Report (migration 0157) ---
  // The closing beat of the incident workflow (Ontario IMS §8 Demobilization → After-Action
  // Report, App A #1). Planning OPENS the report at deactivation, ADDS authored entries
  // (findings / recommendations / debrief notes — every one carries a human author), FINALIZES
  // it into an immutable record, and EXPORTS the PDF via the §4.21 delivery channel. The COP
  // authors nothing (records-not-confers); no entry may name an individual (person-eval ban).
  // Kept in lockstep with the valid_operator_action_types INSERT in migration 0157.
  'open_after_action_report',
  'add_after_action_entry',
  'finalize_after_action_report',
  'export_after_action_report_pdf',
] as const;

export const OperatorActionTypeSchema = z.enum(OPERATOR_ACTION_TYPES);
export type OperatorActionType = z.infer<typeof OperatorActionTypeSchema>;

/** The client-supplied request body. operator_id / timestamp / ip / user-agent
 *  are added server-side and are NOT accepted from the client. */
export const OperatorActionRequestSchema = z.object({
  action_type: OperatorActionTypeSchema,
  session_id: z.string().min(1).max(128),
  /** Action-specific detail (which layer, which entity id, …). Free-form. */
  payload: z.record(z.unknown()).optional(),
  /** COP snapshot at action time: bbox, toggle states, current selection, zoom. */
  context: z.record(z.unknown()).optional(),
});
export type OperatorActionRequest = z.infer<typeof OperatorActionRequestSchema>;
